import { POSITIONS } from "../../../shared/domain/defense";
import type { DefensePosition } from "../../../shared/domain/defense";
import type { PlayerListItem } from "../../shared/types/contracts";
import { Badge } from "../../shared/ui/badge";
import { Button } from "../../shared/ui/button";
import { SectionCard } from "../../shared/ui/section-card";
import { actionKeys } from "../../hooks/actionKeys";
import type { IntentStatusView } from "../../hooks/useIntentStatus";

interface RosterSetupStepProps {
  players: PlayerListItem[];
  activePlayerIds: string[];
  preferences: Record<string, string[]>;
  expandedPlayerId: string | null;
  jerseyHolderPlayerId: string | null;
  editable: boolean;
  onSetAttendance: (playerId: string, active: boolean) => void;
  onTogglePlayerExpanded: (playerId: string) => void;
  onTogglePreference: (playerId: string, position: DefensePosition) => void;
  onSetJerseyHolder: (playerId: string | null) => void;
  onRequestPlayerRemoval: (playerId: string) => void;
  actionStatus: IntentStatusView;
}

export function RosterSetupStep({
  players,
  activePlayerIds,
  preferences,
  expandedPlayerId,
  jerseyHolderPlayerId,
  editable,
  onSetAttendance,
  onTogglePlayerExpanded,
  onTogglePreference,
  onSetJerseyHolder,
  onRequestPlayerRemoval,
  actionStatus,
}: RosterSetupStepProps) {
  return (
    <SectionCard
      title="1 · WHO'S PLAYING"
      description={
        editable
          ? "Set tonight's attendance. Open a player for positions and roster actions."
          : "Attendance and roster removals are locked after first pitch. Player details remain available."
      }
      meta={
        <Badge variant={activePlayerIds.length ? "success" : "neutral"}>
          {activePlayerIds.length} IN
        </Badge>
      }
      contentClassName="px-2 pb-2"
    >
      {players.length ? (
        <ol className="m-0 grid list-none gap-1 p-0">
          {players.map((player) => {
            const isActive = activePlayerIds.includes(player.id);
            const expanded = expandedPlayerId === player.id;
            const preferenceList = preferences[player.id] || [];
            const attendancePending = actionStatus.isPending(
              actionKeys.attendance(player.id),
            );
            const attendanceUnknown =
              actionStatus.phaseFor(actionKeys.attendance(player.id)) ===
              "outcome_unknown";
            const preferencesPending = actionStatus.isPending(
              actionKeys.preferences(player.id),
            );
            const preferencesUnknown =
              actionStatus.phaseFor(actionKeys.preferences(player.id)) ===
              "outcome_unknown";
            const jerseyPending =
              actionStatus.countFor(actionKeys.jerseyGroup) > 0;
            const detailsId = `player-details-${player.id}`;
            return (
              <li
                key={player.id}
                className={[
                  "min-w-0 rounded-[var(--radius-sm)] border transition-colors",
                  expanded
                    ? "border-border bg-surface-raised"
                    : "border-transparent odd:bg-background/35",
                  isActive ? "" : "opacity-70",
                ].join(" ")}
              >
                <div className="flex min-w-0 items-center gap-2 p-1.5">
                  <Button
                    type="button"
                    onClick={() => onSetAttendance(player.id, !isActive)}
                    disabled={!editable}
                    pending={attendancePending}
                    pendingLabel={
                      <span className="sr-only">
                        {attendanceUnknown ? "CHECKING" : "SAVING"}
                      </span>
                    }
                    size="sm"
                    variant={isActive ? "selected" : "outline"}
                    className="w-12 px-0 font-mono"
                    aria-pressed={isActive}
                  >
                    {isActive ? "IN" : "OUT"}
                  </Button>
                  <button
                    type="button"
                    onClick={() => onTogglePlayerExpanded(player.id)}
                    aria-expanded={expanded}
                    aria-controls={detailsId}
                    className="flex h-8 min-w-0 flex-1 items-center gap-1 rounded-[var(--radius-sm)] px-1.5 text-left text-base font-bold outline-none hover:bg-background/40 focus-visible:ring-2 focus-visible:ring-ring/70"
                  >
                    <span className="truncate">{player.name}</span>
                    {player.num != null ? (
                      <span className="shrink-0 font-mono text-xs font-normal text-foreground-muted">
                        #{player.num}
                      </span>
                    ) : null}
                    {player.id === jerseyHolderPlayerId ? (
                      <span
                        className="jersey-wiggle shrink-0"
                        title="strikeout jersey holder"
                      >
                        👕
                      </span>
                    ) : null}
                    <span
                      aria-hidden="true"
                      className="ml-auto shrink-0 text-xs text-foreground-muted"
                    >
                      {expanded ? "▾" : "▸"}
                    </span>
                  </button>
                </div>

                {expanded ? (
                  <div
                    id={detailsId}
                    className="grid gap-2 border-t border-border px-2 py-2 sm:ml-14"
                  >
                    <div>
                      <div className="mb-1 font-mono text-[0.68rem] font-bold tracking-[0.1em] text-warning uppercase">
                        POSITIONS (tap to toggle)
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {POSITIONS.map((position) => {
                          const selected = preferenceList.includes(position);
                          return (
                            <Button
                              key={position}
                              type="button"
                              onClick={() =>
                                onTogglePreference(player.id, position)
                              }
                              size="xs"
                              variant={selected ? "selected" : "outline"}
                              aria-pressed={selected}
                              className="min-w-9 font-mono"
                            >
                              {position}
                            </Button>
                          );
                        })}
                      </div>
                      <div
                        aria-live="polite"
                        className="min-h-4 font-mono text-[0.62rem] font-bold tracking-[0.08em] text-foreground-muted"
                      >
                        {preferencesPending
                          ? preferencesUnknown
                            ? "CHECKING POSITIONS…"
                            : "SAVING POSITIONS…"
                          : ""}
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      {jerseyHolderPlayerId === player.id ? (
                        <>
                          <span className="jersey-text text-xs font-bold">
                            strikeout jersey holder
                          </span>
                          <Button
                            type="button"
                            onClick={() => onSetJerseyHolder(null)}
                            size="xs"
                            variant="outline"
                            pending={actionStatus.isPending(
                              actionKeys.jersey(null),
                            )}
                            pendingLabel={
                              actionStatus.phaseFor(actionKeys.jersey(null)) ===
                              "outcome_unknown"
                                ? "CHECKING…"
                                : "CLEARING…"
                            }
                            disabled={jerseyPending}
                          >
                            CLEAR
                          </Button>
                        </>
                      ) : (
                        <Button
                          type="button"
                          onClick={() => onSetJerseyHolder(player.id)}
                          title="Wear it until the next strikeout"
                          size="xs"
                          variant="outline"
                          className="border-dashed"
                          pending={actionStatus.isPending(
                            actionKeys.jersey(player.id),
                          )}
                          pendingLabel={
                            actionStatus.phaseFor(
                              actionKeys.jersey(player.id),
                            ) === "outcome_unknown"
                              ? "CHECKING…"
                              : "SAVING…"
                          }
                          disabled={jerseyPending}
                        >
                          👕 make jersey holder
                        </Button>
                      )}
                      <Button
                        type="button"
                        onClick={() => onRequestPlayerRemoval(player.id)}
                        disabled={!editable}
                        size="xs"
                        variant="ghost"
                        className="ml-auto text-danger hover:text-danger"
                      >
                        remove from roster
                      </Button>
                    </div>
                  </div>
                ) : null}
              </li>
            );
          })}
        </ol>
      ) : (
        <p className="m-0 px-2 py-4 text-center text-sm text-foreground-muted">
          Add players from Manage to build the roster.
        </p>
      )}
    </SectionCard>
  );
}
